import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import { supabase } from "../lib/supabase";
import { applyThemeAdjustments } from "../utils/themeColorUtils";
import { logger } from "../utils/logger";

/**
 * ShippingType type
 */
export type ShippingType = "free" | "free_over_amount" | "flat";

/**
 * Currency type
 */
export type Currency = "USD" | "EUR" | "GBP" | "CAD" | "AUD";

/**
 * StoreSettings interface
 */
export interface StoreSettings {
  store_name: string;
  store_description: string;
  store_logo_url: string | null;
  tax_rate: number;
  shipping_type: ShippingType;
  shipping_cost: number;
  free_shipping_threshold: number | null;
  currency: Currency;
  store_hours: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  facebook_url: string | null;
  twitter_url: string | null;
  instagram_url: string | null;
  return_policy: string;
  store_location: string | null;
  show_home_ambience_uploader: boolean;
  show_theme_toggle: boolean;
  show_public_reviews: boolean;
  show_home_testimonials: boolean;
  reviews_visibility_updated_at: string | null;
  scroll_thumb_brightness: number;
  enable_loyalty_program: boolean;
  enable_reservations: boolean;
  enable_menu_filters: boolean;
  enable_product_customization: boolean;
  enable_order_tracking: boolean;
  enable_order_feedback: boolean;
  enable_marketing_optins: boolean;
  enable_quick_reorder: boolean;
  show_chefs_picks: boolean;
  theme_contrast: number;
  theme_exposure: number;
  theme_brilliance: number;
  theme_highlights: number;
  theme_shadows: number;
  theme_brightness: number;
  theme_black_point: number;
  theme_saturation: number;
  theme_vibrance: number;
  theme_warmth: number;
  theme_tint: number;
  theme_sharpness: number;
  theme_definition: number;
  theme_vignette: number;
}

/**
 * UpdateSettingsResponse interface
 */
export interface UpdateSettingsResponse {
  success: boolean;
  data?: StoreSettings;
  error?: string;
}

/**
 * StoreSettingsContextValue interface
 */
export interface StoreSettingsContextValue {
  settings: StoreSettings | null;
  loading: boolean;
  updateSettings: (
    updates: Partial<StoreSettings>,
  ) => Promise<UpdateSettingsResponse>;
  refreshSettings: () => Promise<void>;
  calculateShipping: (cartTotal: number) => number;
  calculateTax: (subtotal: number) => number;
  getCurrencySymbol: () => string;
  formatPrice: (amount: number) => string;
}

const StoreSettingsContext = createContext<
  StoreSettingsContextValue | undefined
>(undefined);

/**
 * useStoreSettings hook
 *
 * @returns {StoreSettingsContextValue} Store settings context value
 * @throws {Error} If used outside StoreSettingsProvider
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useStoreSettings = (): StoreSettingsContextValue => {
  const context = useContext(StoreSettingsContext);
  if (!context) {
    throw new Error(
      "useStoreSettings must be used within a StoreSettingsProvider",
    );
  }
  return context;
};

/**
 * Normalize boolean value from various formats
 */
const normalizeBoolean = (value: unknown, fallback = false): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "on", "t"].includes(normalized)) return true;
    if (["false", "0", "no", "off", "", "f"].includes(normalized)) return false;
  }
  if (value === null || value === undefined) return fallback;
  return fallback;
};

/**
 * Clamp brightness value between 0.05 and 1.0
 */
const clampBrightness = (value: unknown, fallback = 0.6): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(1, Math.max(0.05, Number(parsed.toFixed(2))));
};

/**
 * Get default store settings
 */
const getDefaultSettings = (): StoreSettings => ({
  store_name: "Buildfast Shop",
  store_description: "Your one-stop shop for everything you need",
  store_logo_url: null,
  tax_rate: 0.0,
  shipping_type: "flat",
  shipping_cost: 5.0,
  free_shipping_threshold: null,
  currency: "USD",
  store_hours: null,
  contact_email: null,
  contact_phone: null,
  facebook_url: null,
  twitter_url: null,
  instagram_url: null,
  return_policy:
    "We accept returns within 30 days of purchase. Items must be in original condition.",
  store_location: null,
  show_home_ambience_uploader: false,
  show_theme_toggle: false,
  show_public_reviews: false,
  show_home_testimonials: true,
  reviews_visibility_updated_at: null,
  scroll_thumb_brightness: 0.6,
  // Feature flags - all enabled by default
  enable_loyalty_program: false,
  enable_reservations: true,
  enable_menu_filters: false,
  enable_product_customization: false,
  enable_order_tracking: false,
  enable_order_feedback: false,
  enable_marketing_optins: false,
  enable_quick_reorder: false,
  show_chefs_picks: false,
  // Theme Adjustments - defaults (better for light theme)
  theme_contrast: 1.05, // Slightly more contrast
  theme_exposure: 0.0,
  theme_brilliance: 0.0,
  theme_highlights: 0.0,
  theme_shadows: 0.0,
  theme_brightness: 0.95, // Slightly dimmer
  theme_black_point: 0.0,
  theme_saturation: 1.0,
  theme_vibrance: 0.0,
  theme_warmth: 20, // Warm tone
  theme_tint: 0.0,
  theme_sharpness: 0.0,
  theme_definition: 0.0,
  theme_vignette: 0.0,
});

/**
 * Normalize raw settings data - only use defaults for fields that are truly missing
 * This ensures admin settings persist forever once set
 */
const normalizeSettings = (raw: Partial<StoreSettings> = {}): StoreSettings => {
  const defaults = getDefaultSettings();

  // Create a new object that preserves ALL existing values from raw
  // Only use defaults for fields that are completely undefined (never been set)
  const normalized: StoreSettings = {
    // Start with defaults
    ...defaults,
    // Override with raw values - this preserves null values as they were explicitly set
    ...raw,
    // Normalize specific fields that need type conversion - only if defined
    show_home_ambience_uploader:
      raw.show_home_ambience_uploader !== undefined
        ? normalizeBoolean(
            raw.show_home_ambience_uploader,
            defaults.show_home_ambience_uploader,
          )
        : defaults.show_home_ambience_uploader,
    show_theme_toggle:
      raw.show_theme_toggle !== undefined
        ? normalizeBoolean(raw.show_theme_toggle, defaults.show_theme_toggle)
        : defaults.show_theme_toggle,
    show_public_reviews:
      raw.show_public_reviews !== undefined
        ? normalizeBoolean(
            raw.show_public_reviews,
            defaults.show_public_reviews,
          )
        : defaults.show_public_reviews,
    show_home_testimonials:
      raw.show_home_testimonials !== undefined
        ? normalizeBoolean(
            raw.show_home_testimonials,
            defaults.show_home_testimonials,
          )
        : defaults.show_home_testimonials,
    scroll_thumb_brightness:
      raw.scroll_thumb_brightness !== undefined &&
      raw.scroll_thumb_brightness !== null
        ? clampBrightness(
            raw.scroll_thumb_brightness,
            defaults.scroll_thumb_brightness,
          )
        : defaults.scroll_thumb_brightness,
    // Normalize feature flags - preserve explicit false values
    enable_loyalty_program:
      raw.enable_loyalty_program !== undefined
        ? normalizeBoolean(
            raw.enable_loyalty_program,
            defaults.enable_loyalty_program,
          )
        : defaults.enable_loyalty_program,
    enable_reservations:
      raw.enable_reservations !== undefined
        ? normalizeBoolean(
            raw.enable_reservations,
            defaults.enable_reservations,
          )
        : defaults.enable_reservations,
    enable_menu_filters:
      raw.enable_menu_filters !== undefined
        ? normalizeBoolean(
            raw.enable_menu_filters,
            defaults.enable_menu_filters,
          )
        : defaults.enable_menu_filters,
    enable_product_customization:
      raw.enable_product_customization !== undefined
        ? normalizeBoolean(
            raw.enable_product_customization,
            defaults.enable_product_customization,
          )
        : defaults.enable_product_customization,
    enable_order_tracking:
      raw.enable_order_tracking !== undefined
        ? normalizeBoolean(
            raw.enable_order_tracking,
            defaults.enable_order_tracking,
          )
        : defaults.enable_order_tracking,
    enable_order_feedback:
      raw.enable_order_feedback !== undefined
        ? normalizeBoolean(
            raw.enable_order_feedback,
            defaults.enable_order_feedback,
          )
        : defaults.enable_order_feedback,
    enable_marketing_optins:
      raw.enable_marketing_optins !== undefined
        ? normalizeBoolean(
            raw.enable_marketing_optins,
            defaults.enable_marketing_optins,
          )
        : defaults.enable_marketing_optins,
    enable_quick_reorder:
      raw.enable_quick_reorder !== undefined
        ? normalizeBoolean(
            raw.enable_quick_reorder,
            defaults.enable_quick_reorder,
          )
        : defaults.enable_quick_reorder,
    show_chefs_picks:
      raw.show_chefs_picks !== undefined
        ? normalizeBoolean(raw.show_chefs_picks, defaults.show_chefs_picks)
        : defaults.show_chefs_picks,
    // Preserve all other fields as-is (including null values)
    store_name: raw.store_name ?? defaults.store_name,
    store_description: raw.store_description ?? defaults.store_description,
    store_logo_url: raw.store_logo_url ?? defaults.store_logo_url,
    tax_rate: raw.tax_rate ?? defaults.tax_rate,
    shipping_type: raw.shipping_type ?? defaults.shipping_type,
    shipping_cost: raw.shipping_cost ?? defaults.shipping_cost,
    free_shipping_threshold:
      raw.free_shipping_threshold ?? defaults.free_shipping_threshold,
    currency: raw.currency ?? defaults.currency,
    store_hours: raw.store_hours ?? defaults.store_hours,
    contact_email: raw.contact_email ?? defaults.contact_email,
    contact_phone: raw.contact_phone ?? defaults.contact_phone,
    facebook_url: raw.facebook_url ?? defaults.facebook_url,
    twitter_url: raw.twitter_url ?? defaults.twitter_url,
    instagram_url: raw.instagram_url ?? defaults.instagram_url,
    return_policy: raw.return_policy ?? defaults.return_policy,
    store_location: raw.store_location ?? defaults.store_location,
    reviews_visibility_updated_at:
      raw.reviews_visibility_updated_at ??
      defaults.reviews_visibility_updated_at,
    // Theme adjustments - preserve all values
    theme_contrast: raw.theme_contrast ?? defaults.theme_contrast,
    theme_exposure: raw.theme_exposure ?? defaults.theme_exposure,
    theme_brilliance: raw.theme_brilliance ?? defaults.theme_brilliance,
    theme_highlights: raw.theme_highlights ?? defaults.theme_highlights,
    theme_shadows: raw.theme_shadows ?? defaults.theme_shadows,
    theme_brightness: raw.theme_brightness ?? defaults.theme_brightness,
    theme_black_point: raw.theme_black_point ?? defaults.theme_black_point,
    theme_saturation: raw.theme_saturation ?? defaults.theme_saturation,
    theme_vibrance: raw.theme_vibrance ?? defaults.theme_vibrance,
    theme_warmth: raw.theme_warmth ?? defaults.theme_warmth,
    theme_tint: raw.theme_tint ?? defaults.theme_tint,
    theme_sharpness: raw.theme_sharpness ?? defaults.theme_sharpness,
    theme_definition: raw.theme_definition ?? defaults.theme_definition,
    theme_vignette: raw.theme_vignette ?? defaults.theme_vignette,
  };

  return normalized;
};

/**
 * StoreSettingsProviderProps interface
 */
export interface StoreSettingsProviderProps {
  children: ReactNode;
}

/**
 * StoreSettingsProvider Component
 *
 * @param {StoreSettingsProviderProps} props - Component props
 */
export function StoreSettingsProvider({
  children,
}: StoreSettingsProviderProps) {
  // Initialize with defaults immediately so app can render while fetching
  const [settings, setSettings] = useState<StoreSettings | null>(
    getDefaultSettings(),
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [tableExists, setTableExists] = useState<boolean>(false);

  // Fetch store settings with timeout protection
  const fetchSettings = useCallback(async (): Promise<void> => {
    try {
      // Check if Supabase is properly configured
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl || supabaseUrl.includes("placeholder")) {
        logger.warn("Supabase not configured, using default settings");
        setSettings(getDefaultSettings());
        setLoading(false);
        return;
      }

      // Fetch settings with timeout - if it hangs, we'll use defaults
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(
          () => reject(new Error("Settings fetch timeout after 10s")),
          10000,
        ),
      );

      // Use a simpler query - just get the first row (singleton pattern ensures only one)
      const fetchPromise = supabase
        .from("store_settings")
        .select("*")
        .limit(1)
        .maybeSingle();

      let result: { data: unknown; error: unknown };
      try {
        result = await Promise.race([fetchPromise, timeoutPromise]);
      } catch {
        // Timeout occurred - query is hanging
        logger.warn("⚠️ Settings query timed out - using default settings");
        setSettings(getDefaultSettings());
        setLoading(false);
        return;
      }

      const { data, error } = result;

      if (error) {
        logger.error("Error fetching store settings:", error);

        // Type guard for Supabase error
        const supabaseError = error as {
          code?: string;
          message?: string;
          details?: string;
        };
        if (supabaseError.code) {
          logger.error("Error code:", supabaseError.code);
        }
        if (supabaseError.message) {
          logger.error("Error message:", supabaseError.message);
        }
        if (supabaseError.details) {
          logger.error("Error details:", supabaseError.details);
        }

        // Log specific error types for debugging
        if (
          supabaseError.code === "PGRST116" ||
          supabaseError.code === "42P01"
        ) {
          logger.error(
            "⚠️ store_settings table not found. Please run migrations.",
          );
        } else if (supabaseError.code === "42501") {
          logger.error(
            "⚠️ Permission denied. Check RLS policies on store_settings table.",
          );
        } else if (
          supabaseError.message?.includes("timeout") ||
          supabaseError.message?.includes("aborted")
        ) {
          logger.error("⚠️ Request timed out. Check Supabase connection.");
        }

        // Use default settings if fetch fails
        setSettings(getDefaultSettings());
        setTableExists(false); // Table doesn't exist or access denied
      } else if (!data) {
        // maybeSingle() returns null if no rows found
        setSettings(getDefaultSettings());
        setTableExists(false);
      } else {
        setSettings(normalizeSettings(data as Partial<StoreSettings>));
        setTableExists(true); // Mark table as existing for real-time subscription
      }
    } catch (err) {
      // Check if it was a timeout error
      if (err instanceof Error && err.message.includes("timeout")) {
        logger.warn("⚠️ Settings fetch timed out after 5s. Check:");
        logger.warn("  1. Is store_settings table created?");
        logger.warn("  2. Are RLS policies allowing public read?");
        logger.warn("  3. Is Supabase URL correct?");
        logger.warn("  4. Check Network tab for failed requests");
      }

      logger.error("Error in fetchSettings:", err);
      if (err instanceof Error) {
        logger.error("Error type:", err.constructor.name);
        logger.error("Error message:", err.message);
      }
      // Always set default settings on any error to prevent white screen
      setSettings(getDefaultSettings());
      setTableExists(false); // Assume table doesn't exist on error
    } finally {
      // Always set loading to false, even on timeout/error
      setLoading(false);
    }
  }, []);

  // Update store settings (admin only) - MERGE with existing settings to preserve all values
  const updateSettings = useCallback(
    async (
      updates: Partial<StoreSettings>,
    ): Promise<UpdateSettingsResponse> => {
      try {
        if (!settings) {
          throw new Error("Settings not loaded yet");
        }

        // CRITICAL: Merge updates with existing settings to preserve ALL current values
        // This ensures partial updates don't lose existing settings
        const mergedUpdates: Partial<StoreSettings> = {
          ...settings, // Start with ALL current settings
          ...updates, // Override only the fields being updated
        };

        const preparedUpdates: Partial<StoreSettings> = { ...mergedUpdates };
        if (preparedUpdates.scroll_thumb_brightness !== undefined) {
          preparedUpdates.scroll_thumb_brightness = clampBrightness(
            preparedUpdates.scroll_thumb_brightness,
            settings.scroll_thumb_brightness ??
              getDefaultSettings().scroll_thumb_brightness,
          );
        }

        // Store previous state for rollback on error
        const previousSettings: StoreSettings = { ...settings };

        // Optimistically update local state for immediate UI feedback
        setSettings((prev) =>
          normalizeSettings({ ...(prev || {}), ...preparedUpdates }),
        );

        // Save ALL settings to database (not just updates) to ensure persistence
        const { data, error } = await supabase
          .from("store_settings")
          .update(preparedUpdates as never)
          .eq("singleton_guard", true)
          .select()
          .single();

        if (error) {
          // Rollback on error - restore previous state
          logger.error("Failed to update settings, rolling back:", error);
          setSettings(normalizeSettings(previousSettings));
          throw error;
        }

        // Ensure normalized data is set (real-time subscription will also update, but this ensures consistency)
        const normalizedData = normalizeSettings(
          data as Partial<StoreSettings>,
        );
        setSettings(normalizedData);

        logger.log(
          "Settings updated successfully and persisted:",
          Object.keys(updates),
        );
        return { success: true, data: normalizedData };
      } catch (err) {
        const error = err as Error;
        logger.error("Error updating store settings:", err);
        return { success: false, error: error.message };
      }
    },
    [settings],
  ); // Depends on settings - this is fine, context value will update when settings change

  // Calculate shipping cost based on cart total
  const calculateShipping = useCallback(
    (cartTotal: number): number => {
      if (!settings) return 0;

      switch (settings.shipping_type) {
        case "free":
          return 0;
        case "free_over_amount":
          if (
            settings.free_shipping_threshold &&
            cartTotal >= settings.free_shipping_threshold
          ) {
            return 0;
          }
          return settings.shipping_cost || 0;
        case "flat":
        default:
          return settings.shipping_cost || 0;
      }
    },
    [settings],
  );

  // Calculate tax based on subtotal
  const calculateTax = useCallback(
    (subtotal: number): number => {
      if (!settings || !settings.tax_rate) return 0;
      return (subtotal * settings.tax_rate) / 100;
    },
    [settings],
  );

  // Get currency symbol
  const getCurrencySymbol = useCallback((): string => {
    if (!settings) return "$";

    const symbols: Record<Currency, string> = {
      USD: "$",
      EUR: "€",
      GBP: "£",
      CAD: "C$",
      AUD: "A$",
    };

    return symbols[settings.currency] || "$";
  }, [settings]);

  // Format price with currency
  const formatPrice = useCallback(
    (amount: number): string => {
      const symbol = getCurrencySymbol();
      return `${symbol}${Number(amount).toFixed(2)}`;
    },
    [getCurrencySymbol],
  );

  // Load settings on mount
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Set up real-time subscription only after successful fetch
  useEffect(() => {
    // Only set up real-time subscription if table exists and settings loaded successfully
    if (!tableExists || !settings) {
      return;
    }

    // Set up real-time subscription for settings changes
    const channel = supabase
      .channel("store-settings-changes")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "store_settings",
          filter: "singleton_guard=eq.true",
        },
        (payload: { new: Partial<StoreSettings> }) => {
          // Defer state update to avoid blocking message handler
          if (
            typeof window !== "undefined" &&
            "requestIdleCallback" in window
          ) {
            requestIdleCallback(() => {
              setSettings(normalizeSettings(payload.new));
            });
          } else {
            // Use setTimeout to defer update
            setTimeout(() => {
              setSettings(normalizeSettings(payload.new));
            }, 0);
          }
        },
      )
      .subscribe((status: string) => {
        // Suppress errors in development - they're expected if table doesn't exist
        if (status === "CHANNEL_ERROR") {
          if (import.meta.env.DEV) {
            // Only log in dev, don't show as error
            logger.log(
              "Real-time subscription not available (table may not exist or RLS blocking)",
            );
          }
        } else if (status === "TIMED_OUT") {
          // Don't retry if table doesn't exist - it will just fail again
          if (tableExists) {
            logger.warn("Real-time subscription timed out - retrying...");
            // Defer retry to avoid blocking
            if (
              typeof window !== "undefined" &&
              "requestIdleCallback" in window
            ) {
              requestIdleCallback(() => {
                setTimeout(() => {
                  channel.subscribe();
                }, 2000);
              });
            } else {
              setTimeout(() => {
                channel.subscribe();
              }, 2000);
            }
          }
        } else if (status === "CLOSED") {
          // Subscription closed - normal cleanup
        }
      });

    // Cleanup subscription - optimized for fast cleanup
    return () => {
      // Use requestIdleCallback or setTimeout to avoid blocking close handler
      if (typeof window !== "undefined" && "requestIdleCallback" in window) {
        requestIdleCallback(() => {
          try {
            supabase.removeChannel(channel);
          } catch (error) {
            // Silently handle cleanup errors - don't block
            if (import.meta.env.DEV) {
              logger.log("Cleanup error (non-critical):", error);
            }
          }
        });
      } else {
        // Fallback: use setTimeout with 0 delay to defer cleanup
        setTimeout(() => {
          try {
            supabase.removeChannel(channel);
          } catch (error) {
            // Silently handle cleanup errors - don't block
            if (import.meta.env.DEV) {
              logger.log("Cleanup error (non-critical):", error);
            }
          }
        }, 0);
      }
    };
  }, [tableExists, settings]);

  // Apply theme adjustments as CSS variables when settings change
  useEffect(() => {
    if (settings) {
      applyThemeAdjustments(settings as unknown as Record<string, unknown>);
    }
  }, [settings]);

  // Memoize context value - functions are already memoized with useCallback
  // We include them in deps to satisfy React, but they only change when their own deps change
  const value: StoreSettingsContextValue = useMemo(
    () => ({
      settings: settings ?? getDefaultSettings(), // Ensure never null for page refresh reliability
      loading,
      updateSettings,
      refreshSettings: fetchSettings,
      calculateShipping,
      calculateTax,
      getCurrencySymbol,
      formatPrice,
    }),
    [
      settings,
      loading,
      updateSettings,
      fetchSettings,
      calculateShipping,
      calculateTax,
      getCurrencySymbol,
      formatPrice,
    ],
  );

  return (
    <StoreSettingsContext.Provider value={value}>
      {children}
    </StoreSettingsContext.Provider>
  );
}
